import { LinearProgress } from "@mui/material";
import { useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { useReactToPrint } from "react-to-print";
import { toast } from "react-toastify";
import { io } from 'socket.io-client';
import api, { downloadFile } from "../../BrandOnBoarding/api";
import AIGenerated from "../../Component/AIGenerated/AIGeneratedV2";
import Header from "../../Component/Header/Header";
import PDFFooter from "../../Component/PDFFooter/PDFFooter";
import PDFHeader from "../../Component/PDFHeader/PDFHeader";
import MUIButton from "../../Component/UI/MUIButton/MUIButton";
import Tabs from "../../Component/UI/Tabs/Tabs";
import { AuthContext } from "../../context/auth-context";
import HTMLReportViewer from "../BrandAudit/HTMLReportTab/HTMLReportViewer";
import { useDailyLimits } from "../../hooks/useDailyLimits";
import ProductSpecs from "../CategoryDiscovery/ProductSpecs/ProductSpecs";
import RealtimeActions from "./Actions/Actions";
import BrandScore from "./BrandScore/BrandScore";
import CategoryDimensions from "./CategoryDimensions/CategoryDimensions";
import Competitors from "./Competitors/Competitors";
import CoreAttributes from "./CoreAttributes/CoreAttributes";
import { DiscoveryContext } from "./discovery-context";
import classes from "./Discovery.module.css";
import Form from "./Form";
import History from "./History/History";
import Sources from "./Sources/Sources";
import Taxonomy from "./Taxonomy/Taxonomy";
import { getReportDownloadUrl, updateValidatedSources } from "../../utils/helpers";
import { getMetricsRecord } from "../../utils/constant"
import { ENTITY_LABELS } from "../../utils/labelMapping";

export default function DiscoveryV2() {
  const [inputValues, setInputValues] = useState({
    brand: "",
    category: "",
    market: "",
    llm: "",
    type: "brand",
  });
  const [tabSelected, setTabSelected] = useState("overview");
  const [externalReportLoading, setExternalReportLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [warningText, setWarningText] = useState("");
  const [childKey, setChildKey] = useState(0);
  const discoveryCtx = useContext(DiscoveryContext);
  const socketRef = useRef(null);
  const transactionIdRef = useRef(null);
  const discoveryCtxRef = useRef(discoveryCtx);
  const pollingRef = useRef(null);
  const [cancelTransaction, setCancelTransaction] = useState(false);
  const [isHistoryRefresh, setIsHistoryRefresh] = useState(false);
  const [processRetry, setProcessRetry] = useState(false);
  const [page, setPage] = useState(1);
  const [inlineViewer, setInlineViewer] = useState(null);
  const remountChild = () => {
    setChildKey((prev) => prev + 1);
  };

  const {
    launchText,
    updateLaunches,
    launchesRemaining,
    isSpecial,
    frequency: launchLimitFrequency,
  } = useDailyLimits("Discovery");

  const abortControllerRef = useRef(null);
  const location = useLocation();
  const metricsRecord = getMetricsRecord();
  const entity = discoveryCtx.entity;
  const discoveryType = discoveryCtx.type || "brand";
  const discoveryTotalCount = 22;
  const pdfRef = useRef();
  const rankScoreAbortCtrRef = useRef(null);
  const { user } = useContext(AuthContext);
  const webSocketBaseURL = process.env.REACT_APP_ORCHESTRATOR_BASE_URL;
  let brandData = discoveryCtx.brand_scores;
  let brandDataCompetitors = brandData.slice(1);
  const highestFavorability = Math.max(...brandDataCompetitors.map((item) => item?.favorability ?? 0));
  const highestSentiment = brandDataCompetitors.find(
    (item) => item?.favorability === highestFavorability
  );

  function tabChangeHandler(val) {
    setTabSelected(val);
  }
  let currentBrandSnippets = [];
  let currentBrandThemes = [];
  const coreAttributeMapRef = useRef({});
  const loadSocketData = (socketResponse, requestObj, currentTransactionId, showOldSourcesRecord) => {
    if (!socketResponse || typeof socketResponse !== 'object') {
      console.error("Invalid socket response:", socketResponse);
      return;
    }

    let { type, request, response, category, llm, status, transactionId, isValidated } = socketResponse;

    if (!type || !request) {
      console.error("Incomplete socket response:", response);
      return;
    }
    if (status !== 'COMPLETED') {
      return false
    }
    if (parseInt(transactionId) !== parseInt(currentTransactionId)) return;

    const onlyIfValidated = (data, empty = Array.isArray(data) ? [] : {}) => {
      if (showOldSourcesRecord) {
        if (Array.isArray(data)) return data;
        if (data == null) return [];
        return [data];
      }

      return isValidated ? data : empty;
    };

    console.log("LoadedDiscoveryResponse", response)
    switch (type) {
      case "rank":
        if (!response) return console.warn("Missing response for rank");
        response = { ...response, request_id: socketResponse.id, isValidated };
        const sources = onlyIfValidated(response?.sources?.rank || [], []);
        discoveryCtx.updateSources({ sources, showOldSourcesRecord, type: metricsRecord[type] });
        discoveryCtx.updateRankScores(response);
        break;

      case "sentiment":
        if (!response) return console.warn("Missing response/Transaction for sentiment");

        const entity = { ...response?.data }?.[0];
        const sentimentSources = onlyIfValidated(response?.sources?.sentiment || [], []);
        const updatedExtras = !showOldSourcesRecord ? updateValidatedSources(response.extras) : response.extras;
        const brandRes = response?.error
          ? { error: "something went wrong" }
          : {
            name: entity?.name || "Unknown",
            favorability: entity?.score ?? 0,
            extras: updatedExtras,
            sentimentSources,
            isValidated
          };

        let index = -1;
        try {
          requestObj.competitor?.[request.category]?.forEach((item, idx) => {
            if (item.name?.toLowerCase() == entity?.name?.toLowerCase()) {
              index = idx;
            }
          }
          );
          if (requestObj.brand === entity?.name) index = -1;
        } catch (err) {
          console.warn("Error calculating sentiment index:", err);
        }

        discoveryCtx.updateBrandScores({
          data: brandRes,
          index: index + 1,
          sentimentSources,
          isValidated
        });
        discoveryCtx.updateSources({ sources: sentimentSources, showOldSourcesRecord, type: metricsRecord[type] });
        break;


      case "signalStrength":
        if (!response) return console.warn("Missing response for signalStrength");
        const negativeAttributes = response?.aggregatedNegatives || [];
        const positiveAttributes = response?.aggregatedPositives || [];

        let sourcesPositiveArray = onlyIfValidated(
          response?.aggregatedPositiveSources || [],
          []
        );
        let sourcesNegativeArray = onlyIfValidated(
          response?.aggregatedNegativeSources || [],
          []
        );

        if (showOldSourcesRecord) {
          sourcesPositiveArray = sourcesPositiveArray.map((url) => ({ [url]: 1 }));
          sourcesNegativeArray = sourcesNegativeArray.map((url) => ({ [url]: 1 }));
        }

        discoveryCtx.updatePositiveNegativeAttributes({
          name: socketResponse.brand,
          positives: positiveAttributes.slice(0, 10),
          negatives: negativeAttributes.slice(0, 10),
          baseResponses: response?.baseResponses || {}
        });

        discoveryCtx.updateSources({ sources: sourcesPositiveArray, showOldSourcesRecord, type: "Positive Attributes" });
        discoveryCtx.updateSources({ sources: sourcesNegativeArray, showOldSourcesRecord, type: "Negative Attributes" });
        break;

      case "categoryDimension":
        if (!response) return console.warn("Missing response for categoryDimension");

        const normalize = (src) =>
          Array.isArray(src)
            ? onlyIfValidated(src, [])
            : onlyIfValidated(src ? [src] : [], []);

        const sourcesCategory = normalize(response?.sources?.category);
        const sourcesDim = normalize(response?.sources?.dimension);

        const sourcesCategoryDim = [...sourcesCategory, ...sourcesDim];

        discoveryCtx.updateCategoryDimensions({
          data: response?.data || [],
          summary: response?.summary || "",
          sources: sourcesCategory,
          baseResponses:response?.baseResponses || {},
          isValidated,
        });

        discoveryCtx.updateSources({ sources: sourcesCategoryDim, showOldSourcesRecord, type: metricsRecord[type] });
        break;

      case "taxonomy":
        if (!response) return console.warn("Missing response for taxonomy");
        response = { ...response, isValidated };
        discoveryCtx.updatedTaxonomies(response);
        break;

      case "core-attribute":
        if (!response || !category || !llm) return console.warn("Missing data for core-attribute");

        const key = `${llm}__${category}`;

        if (!coreAttributeMapRef.current[key]) {
          coreAttributeMapRef.current[key] = [];
        }

        const newProductName = (response?.brandName || response?.products?.[0]?.name || "")?.toLowerCase().trim();
        const alreadyExists = coreAttributeMapRef.current[key].some(
          (item) =>
            (item?.brandName || item?.products?.[0]?.name || "").toLowerCase().trim() === newProductName
        );
        if (!alreadyExists && newProductName) {
          coreAttributeMapRef.current[key].push(response);
        }
        let competitors = requestObj?.competitor?.[category].map((item) => item.name);
        let brand = requestObj?.brand;
        if (competitors) {
          competitors.unshift(brand);
        } else {
          competitors = [];
        }
        const expectedCount = competitors?.length ?? 0;
        if (coreAttributeMapRef.current[key].length === expectedCount) {
          const group = coreAttributeMapRef.current[key];
          let coreAttributeSummary = "";
          const products = [];
          const sources = [];

          const category_averages = group[0]?.category_averages;

          for (const res of group) {
            if (!res?.error && Array.isArray(res?.products)) {
              coreAttributeSummary += res.products?.[0]?.summary || "";
              coreAttributeSummary += "\n\n";
              const productSources = onlyIfValidated(res?.sources?.product || [], []);
              products.push({
                ...res.products?.[0],
                brandName: res.brandName,
                sources: productSources,
                baseResponses: res.baseResponses || null
              });
              if (showOldSourcesRecord || isValidated) {
                const normalize = (src) =>
                  Array.isArray(src)
                    ? src
                    : src
                      ? [src]
                      : [];

                const categorySources = normalize(res?.sources?.category);
                const productSources = normalize(res?.sources?.product);

                sources.push(...categorySources, ...productSources);
              }
            }
          }

          let sortedProducts = [];
          let baseResponsesArray = [];
          for (const company of competitors) {
            const normalizedCompany = company.toLowerCase().replace(/\s+/g, "");
            for (const product of products) {
              const normalizedProductName = (product?.brandName || product?.name || "")?.toLowerCase()?.replace(/\s+/g, "");
              if (normalizedProductName === normalizedCompany) {
                // Extract baseResponses and add to separate array
                if (product.baseResponses && typeof product.baseResponses === 'object') {
                  const baseResObj = product.baseResponses;

                  // Create one object per brand with array of responses
                  if (baseResObj.baseResponses && Array.isArray(baseResObj.baseResponses)) {
                    baseResponsesArray.push({
                      brandName: product.brandName,
                      basePrompt: baseResObj.basePrompt || "",
                      baseResponse: baseResObj.baseResponses.map(item => ({
                        baseRes: item.baseRes || ""
                      }))
                    });
                  }
                }

                // Remove baseResponses from product before adding to sortedProducts
                const { baseResponses, ...productWithoutBaseResponses } = product;
                sortedProducts.push(productWithoutBaseResponses);
                break;
              }
            }
          }

          discoveryCtx.updateCoreAttributes({
            category_averages,
            products: sortedProducts,
            summary: coreAttributeSummary,
            isValidated,
            baseResponses: baseResponsesArray,
          });

          discoveryCtx.updateSources({ sources, showOldSourcesRecord, type: metricsRecord[type] });

          delete coreAttributeMapRef.current[key];
        }
        break;

      case "description":
        if (!response?.data) return console.warn("Missing response.data for description");
        discoveryCtx.updateBrandDescription(response.data);
        break;

      case "theme":
      case "snippet": {
        const isTheme = type === "theme";
        const dataSnippet = response?.data?.llm ? response?.data : response?.data?.data;

        if (!Array.isArray(response?.data?.data)) {
          console.warn(`${type} response is not a valid array:`, dataSnippet);
          return;
        }

        if (!llm || !category) {
          console.warn("Missing llm/category for", type);
          return;
        }

        const targetStore = isTheme ? currentBrandSnippets : currentBrandThemes;
        const contextMethod = isTheme
          ? discoveryCtx.updateBrandSnippets
          : discoveryCtx.updatedBrandThemes;
        if (!targetStore) targetStore = {};
        if (!targetStore) targetStore = [];
        targetStore.push(dataSnippet)
        contextMethod({
          res: targetStore
        });

        break;
      }

      default:
        console.warn(`Unhandled socket data type: ${type}`);
    }
  };
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const id = searchParams.get("id");
    const type = searchParams.get("type"); // this will be "brand" or "product"
    const customer_id = searchParams.get("customer_id");
    if (id && type && customer_id && !cancelTransaction) {
      loadDiscovery(id, type, customer_id, false);
    }
    if (discoveryCtx.abort_launch) {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (rankScoreAbortCtrRef.current) {
        rankScoreAbortCtrRef.current.abort();
      }
    }
  }, [discoveryCtx.abort_launch]);

  async function loadDiscovery(id, type, customer_id, refreshRecord) {
    try {

      api.get(
        `${webSocketBaseURL}/transaction/discovery/${customer_id}/${id}`
      ).then((response) => {
        if (response?.data?.transactionStatus !== "PENDING") {
          const { totalCount, completedTaskCount, transactionStatus, sourceValidationStatus, task = [] } = response?.data;
          if (refreshRecord) {
            currentBrandSnippets = [];
            currentBrandThemes = [];
            coreAttributeMapRef.current = {};
            discoveryCtx.refreshState()
          } else {
            discoveryCtx.resetState();
          }
          discoveryCtx.updateTransactionId(id)
          discoveryCtx.updateId(id);
          discoveryCtx.updatedLaunched(true);
          if (transactionStatus === "COMPLETED" || transactionStatus === "RETRY" || completedTaskCount === totalCount) {
            // setIsHistoryRefresh(prev => !prev);
            setProcessRetry(false);
            stopPolling();
            discoveryCtx.updateLoading(false);
          }
          discoveryCtx.updateTransactionStatus(transactionStatus);
          discoveryCtx.updateSourceValidationStatus(sourceValidationStatus);
          discoveryCtx.updateStatus({ totalCount, completedTaskCount });
          discoveryCtx.updateType(type);
          let requestObj = task[0]?.request_object
          if (!requestObj) {
            return;
          }
          let competitors = requestObj.competitor[requestObj.category];
          competitors = [{ name: requestObj.brand, domain: requestObj.brandDomainName, variants: [], display_name: requestObj.brand }, ...competitors];
          discoveryCtx.updatedCompetitors(competitors);
          discoveryCtx.updateEntity(requestObj.brand);
          discoveryCtx.updateLanguage(requestObj.language === "Italian" ? "it" : "en")
          discoveryCtx.updateMarket(requestObj.market.join(','));
          discoveryCtx.updateCategory(requestObj.category[0]);
          discoveryCtx.updateCategoryType(requestObj?.categoryType);
          discoveryCtx.updateLLM(requestObj.llm[0]);
          discoveryCtx.updateName(`${requestObj.brand} ${ENTITY_LABELS.brand} Overview`);
          const showOldSourcesRecord = sourceValidationStatus === "PENDING";
          for (const record of task) {
            loadSocketData(record, requestObj, id, showOldSourcesRecord)
          }
        }
      })
        .catch((error) => {
          console.error("API call failed:", error);
          if (error.message === "Network Error") {
            toast.warn("Please check your network connection");
          } else if (error.code === "ECONNABORTED") {
            toast.warn("Request timed out. Please try again");
          } else {
            toast.error("Failed to load discovery.");
          }
        })
    } catch (error) {
      toast.error(
        "Failed to load discovery."
      );
    } finally {
      setExternalReportLoading(false);
    }
  }

  let coreAttributesSummary = "";
  let categoryDimensionsSummary = "";
  let productDimensionSummary = "";
  if (
    discoveryCtx?.core_attributes &&
    Object.keys(discoveryCtx?.core_attributes).length > 0
  ) {
    coreAttributesSummary = discoveryCtx.core_attributes.summary;
  }
  if (
    discoveryCtx?.category_dimensions &&
    Object.keys(discoveryCtx?.category_dimensions).length > 0
  ) {
    categoryDimensionsSummary = discoveryCtx.category_dimensions.summary;
  }

  if (
    discoveryCtx?.taxonomies?.data &&
    Object.keys(discoveryCtx?.taxonomies?.data).length > 0
  ) {
    productDimensionSummary = discoveryCtx.taxonomies?.data?.summary;
  }

  let competitors = discoveryCtx.competitors;

  const exportToPDF = useReactToPrint({
    contentRef: pdfRef,
    documentTitle: `${entity} Overview`,
    removeAfterPrint: true,
    pageStyle: `
  @page {
    size: 380mm 430mm;
    margin-left: 10mm;
    margin-right: 10mm;
    margin-bottom: 5mm;
    margin-top: 15mm;
  }
  @media print {
    html, body {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      margin: 0 !important;
      padding: 0 !important;
      overflow: visible !important;
      width: 100% !important;
    }
    #print-pdf {
      padding: 0 !important;
      margin: 0 !important;
      width: 100% !important;
      overflow: visible !important;
      box-sizing: border-box !important;
    }
 
    * {
      overflow: visible !important;
      box-sizing: border-box !important;
      max-width: 100% !important;
      word-wrap: break-word !important;
      overflow-wrap: break-word !important;
    }
 
    .no-print {
      display: none !important;
    }
 
    .Toastify,
    .Toastify__toast-container,
    .Toastify__toast {
      display: none !important;
    }
 
    #print-pdf .section:last-of-type,
    #print-pdf > *:last-child {
      margin-bottom: 0 !important;
      padding-bottom: 0 !important;
    }
 
    .MuiModal-root,
    .MuiModal-backdrop,
    .MuiDrawer-root,
    .MuiPopover-root,
    .MuiPopper-root,
    .MuiTooltip-popper,
    .MuiSnackbar-root,
    [role="tooltip"] {
      display: none !important;
    }
 
    /* Sources: show only what's visible on screen + never split across pages.
       break-inside:avoid moves the entire Sources block to the next page if
       it doesn't fit in the remaining space on the current page. */
    .pdf-sources-section {
      overflow: hidden !important;
      max-height: 450px !important;
      break-inside: avoid !important;
      page-break-inside: avoid !important;
    }
 
    /* Prevent any .section card (Competitors, Sources, RealtimeActions)
       from splitting mid-card across a page boundary. */
    .section {
      break-inside: avoid !important;
      page-break-inside: avoid !important;
    }
 
    /* Prevent the table+summary two-panel block from splitting. */
    .section_summary {
      break-inside: avoid !important;
      page-break-inside: avoid !important;
    }
 
    .discovery-ui-table          { display: none  !important; }
    .discovery-print-table       { display: block !important; }
    .discovery-print-competitors { display: block !important; }
  }
`,

  });

  async function generateReport() {
    try {
      setLoading(true);
      const frontend_build_version = localStorage.getItem(
        "frontend_build_version"
      );
      const token = localStorage.getItem("token");
      const user = localStorage.getItem("user");
      const payload = {
        isBrand: discoveryType === "brand",
        request_type: "discoveryV2",
        transactionID: discoveryCtx.state.transactionId,
        CompetitorName: highestSentiment.name,
        brand: entity,
        category: discoveryCtx.category,
        market: discoveryCtx.market,
        primaryLLM: discoveryCtx.llm,
        frontend_build_version,
        token,
        user,
      };
      console.log(payload);

      // const blob = await api.post(
      //   "/llm-reporting/generate/discovery",
      //   payload,
      //   {
      //     responseType: "blob",
      //   }
      // );
      // const url = window.URL.createObjectURL(blob.data);
      // const a = document.createElement("a");
      // a.href = url;
      // a.download = "Discovery.pptx";
      // document.body.appendChild(a);
      // a.click();
      // window.URL.revokeObjectURL(url);

      const res = await api.post("/llm-reporting/generate/discovery", payload);
      const { jobId } = res.data || {};
      const downloadUrl = getReportDownloadUrl(jobId);
      if (downloadUrl && jobId) {
        // window.location.assign(downloadUrl);
        const filename = discoveryType === "brand" ? 'brand-discovery-report.pptx' : 'product-discovery-report.pptx';
        await downloadFile(
          `/llm-reporting/download/${jobId}`,
          `${filename}` // or whatever filename you want
        );
        toast.success("Report generated successfully");
      } else {
        throw new Error("No JobId returned");
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  async function retryDiscovery() {
    try {
      const transactionId = discoveryCtx.state.transactionId
      if (!transactionId) throw new Error("Invalid record: transactionId not found");
      setPage(1);
      setIsHistoryRefresh(prev => !prev);
      setProcessRetry(true);
      discoveryCtx.updateTransactionStatus("RUNNING")
      const response = await fetch(
        `${webSocketBaseURL}/retry-transaction-request/${transactionId}`
      );
      retryPolling(transactionId, discoveryCtx.type)
      return response?.data;
    } catch (error) {
      console.error("Retry API failed:", error);
      throw error;
    }
  }

  useEffect(() => {
    if (!socketRef.current) {
      const isProd = process.env.REACT_APP_ENV === "production";

      const socketOptions = {
        reconnectionAttempts: 5,
      };

      const socketUrl = process.env.REACT_APP_SOCKET_BASE_URL;

      if (isProd) {
        socketOptions.path = "/ows/";
      } else {
        socketOptions.transports = ["websocket"];
      }

      // Initialize socket
      socketRef.current = io(socketUrl, socketOptions);

      socketRef.current.on("connect", () => {
        console.log("Socket connected:", socketRef.current.id);
      });

      socketRef.current.on("disconnect", () => {
        console.warn("Socket disconnected");
      });

      socketRef.current.on("live-data", handleSocketData);
    }

    const handleOnline = () => {
      if (socketRef.current) {
        if (!socketRef.current.connected && !socketRef.current.active) {
          console.log("Network back online, reconnecting socket...");
          socketRef.current.connect();
        } else {
          console.log("Socket already connected or reconnecting, skipping...");
        }
      }
    };

    window.addEventListener("online", handleOnline);

    return () => {
      // Optional: Cleanup if needed
      // socketRef.current?.disconnect();
      window.removeEventListener("online", handleOnline);
      transactionIdRef.current = null
    };
  }, []);

  useEffect(() => {
    if (discoveryCtx.state.transactionId) {
      transactionIdRef.current = discoveryCtx.state.transactionId;
    }
    else {
      transactionIdRef.current = null
    }
  }, [discoveryCtx.state.transactionId]);

  useEffect(() => {
    discoveryCtxRef.current = discoveryCtx;
  }, [discoveryCtx]);

  const handleSocketData = (data) => {
    if (!data) {
      console.error("Received null or undefined socket data.");
      return;
    }
    let transactionId, type, request, response, category, llm, transaction, completedTaskCount, isValidated;

    if (Array.isArray(data?.coreAttributeData)) {
      if (!data?.coreAttributeData[0]) {
        console.error("Array socket data is empty or invalid:", data?.coreAttributeData);
        return;
      }
      transactionId = data?.coreAttributeData[0]?.transaction?.transaction_id;
      type = data?.coreAttributeData[0]?.type;
      completedTaskCount = data?.completedTaskCount;
      isValidated = data?.isValidated
    } else {
      ({ type, request, response, category, llm, transaction, completedTaskCount, isValidated } = data);
      transactionId = transaction?.transaction_id ? transaction?.transaction_id : data?.transactionId;
    }

    const currentContextTxnId = transactionIdRef.current;
    if (transactionId !== currentContextTxnId) return;
    if (data?.status === "Transaction Successfull" || data?.status === "Transaction failed") {
      setIsHistoryRefresh(prev => !prev);
      setProcessRetry(false);
      stopPolling();
      discoveryCtx.updateTransactionStatus(data?.status === "Transaction Successfull" ? "COMPLETED" : "RETRY")
      discoveryCtx.updateLoading(false);
    }
    if (data?.sourceValidationStatus) {
      setIsHistoryRefresh(prev => !prev);
      discoveryCtx.updateSourceValidationStatus(data?.sourceValidationStatus)
    }
    discoveryCtx.updateStatus({ completedTaskCount })

    const onlyIfValidated = (data, empty = Array.isArray(data) ? [] : {}) =>
      isValidated ? data : empty;

    console.log("DiscoverySocketData", data)
    switch (type) {
      case "rank":
        if (!response) return console.warn("Missing response for 'rank'");
        response = { ...response, request_id: data.id, isValidated };
        const sources = onlyIfValidated(response?.sources?.rank || [], []);
        discoveryCtx.updateSources({ sources, type: metricsRecord[type] });
        discoveryCtx.updateRankScores(response);
        break;

      case "sentiment":
        if (!response || !transaction || !request?.category) {
          console.warn("Invalid sentiment data:", response, transaction);
          return;
        }

        const entity = response?.data?.[0];
        const sentimentSources = onlyIfValidated(response?.sources?.sentiment || [], []);
        const updatedExtras = updateValidatedSources(response.extras);
        const brandRes = response?.error
          ? { error: "something went wrong" }
          : {
            name: entity?.name || "Unknown",
            favorability: entity?.score ?? 0,
            extras: updatedExtras,
            sentimentSources,
            isValidated
          };

        const originalCompetitors = discoveryCtxRef.current?.competitors || [];
        const competitors = originalCompetitors.slice(1);
        let index = competitors.findIndex((item) => item.name?.toLowerCase() == entity?.name?.toLowerCase());
        if (transaction?.brand === entity?.name) index = -1;

        discoveryCtx.updateBrandScores({
          data: brandRes,
          index: index + 1,
          sentimentSources,
          isValidated
        });
        discoveryCtx.updateSources({ sources: sentimentSources, type: metricsRecord[type] });
        break;



      case "signalStrength":
        if (!response) return console.warn("Missing response for signalStrength");
        const negativeAttributes = response?.aggregatedNegatives || [];
        const positiveAttributes = response?.aggregatedPositives || [];

        let sourcesPositiveArray = onlyIfValidated(
          response?.aggregatedPositiveSources || [],
          []
        );
        let sourcesNegativeArray = onlyIfValidated(
          response?.aggregatedNegativeSources || [],
          []
        );


        discoveryCtx.updatePositiveNegativeAttributes({
          name: data.brand,
          positives: positiveAttributes.slice(0, 10),
          negatives: negativeAttributes.slice(0, 10),
          baseResponses: response?.baseResponses || {}
        });

        discoveryCtx.updateSources({ sources: sourcesPositiveArray, type: "Positive Attributes" });
        discoveryCtx.updateSources({ sources: sourcesNegativeArray, type: "Negative Attributes" });
        break;

      case "categoryDimension":
        if (!response) return console.warn("Missing response for 'categoryDimension'");
        const sourcesCategory = Array.isArray(response?.sources?.category)
          ? onlyIfValidated(response?.sources?.category || [], [])
          : [];

        const sourcesDim = Array.isArray(response?.sources?.dimension)
          ? onlyIfValidated(response?.sources?.dimension || [], [])
          : [];

        const sourcesCategoryDim = [...sourcesCategory, ...sourcesDim];

        discoveryCtx.updateCategoryDimensions({
          data: response?.data || [],
          summary: response?.summary || "",
          sources: sourcesCategory,
          baseResponses:response?.baseResponses || {},
          isValidated
        });

        discoveryCtx.updateSources({ sources: sourcesCategoryDim, type: metricsRecord[type] });
        break;

      case "taxonomy":
        if (!response) return console.warn("Missing response for taxonomy");
        response = { ...response, isValidated };
        discoveryCtx.updatedTaxonomies(response);
        break;

      case "core-attribute":
        if (!Array.isArray(data?.coreAttributeData)) {
          console.warn("Expected array for core-attribute:", data?.coreAttributeData);
          return;
        }
        const expectedCount = discoveryCtxRef.current?.competitors?.length ?? 0;
        // Grouping by category + llm
        if (data?.coreAttributeData.length == expectedCount) {
          const groupedByCategoryAndLlm = data?.coreAttributeData.reduce((acc, item) => {
            const category = item?.category;
            const llm = item?.llm;
            if (!category || !llm) return acc;

            const key = `${category}__${llm}`; // unique key per group
            if (!acc[key]) acc[key] = [];
            acc[key].push(item);

            return acc;
          }, {});

          // Debug log
          console.log("CoreAttributeData", data?.coreAttributeData)

          // Process each group
          Object.entries(groupedByCategoryAndLlm).forEach(([key, items]) => {

            const resData = items
              .filter((res) => res?.response)
              .map((res) => res.response);

            let coreAttributeSummary = "";
            let category_averages = resData[0]?.category_averages || {};
            let products = [];
            const sources = [];

            for (let res of resData) {
              if (!res?.error) {
                coreAttributeSummary += res.products?.[0]?.summary || "";
                coreAttributeSummary += "\n\n";
                const productSources = onlyIfValidated(res?.sources?.product || [], []);
                products.push({
                  ...res.products?.[0],
                  brandName: res.brandName,
                  sources: productSources,
                  baseResponses: res.baseResponses || null
                });
                if (isValidated) {
                  const categorySources = Array.isArray(res?.sources?.category)
                    ? res.sources.category
                    : [];
                  const productSources = Array.isArray(res?.sources?.product)
                    ? res.sources.product
                    : [];

                  sources.push(...categorySources, ...productSources);
                }
              }
            }

            const originalCompetitors =
              discoveryCtxRef.current?.competitors.map((item) => item.name) || [];
            const sortedProducts = [];
            let baseResponsesArray = [];
            originalCompetitors.forEach((company) => {
              const normalizedCompany = company.toLowerCase().replace(/\s+/g, "");
              for (const product of products) {
                const normalizedProductName = (product?.brandName || product?.name || "")?.toLowerCase()?.replace(/\s+/g, "");
                if (normalizedProductName === normalizedCompany) {
                  // Extract baseResponses and add to separate array
                  if (product.baseResponses && typeof product.baseResponses === 'object') {
                    const baseResObj = product.baseResponses;

                    // Create one object per brand with array of responses
                    if (baseResObj.baseResponses && Array.isArray(baseResObj.baseResponses)) {
                      baseResponsesArray.push({
                        brandName: product.brandName,
                        basePrompt: baseResObj.basePrompt || "",
                        baseResponse: baseResObj.baseResponses.map(item => ({
                          baseRes: item.baseRes || ""
                        }))
                      });
                    }
                  }

                  // Remove baseResponses from product before adding to sortedProducts
                  const { baseResponses, ...productWithoutBaseResponses } = product;
                  sortedProducts.push(productWithoutBaseResponses);
                  break;
                }
              }
            });


            discoveryCtx.updateCoreAttributes({
              category_averages,
              products: sortedProducts,
              summary: coreAttributeSummary,
              isValidated,
              baseResponses: baseResponsesArray,
            });

            discoveryCtx.updateSources({ sources, type: metricsRecord[type] });
          });
        }
        break;


      case "description":
        if (!response?.data) return console.warn("Missing response.data for description");
        discoveryCtx.updateBrandDescription(response.data);
        break;

      case "theme":
      case "snippet": {
        const isTheme = type === "theme";
        const snippetData = response?.data;

        if (!Array.isArray(response?.data?.data)) {
          console.warn(`${type} data not in expected array format:`, snippetData);
          return;
        }

        if (!llm || !category) {
          console.warn("Missing llm or category for theme/snippet");
          return;
        }
        const store = isTheme
          ? discoveryCtxRef.current?.brand_snippets
          : discoveryCtxRef.current?.brand_themes;

        const updateFn = isTheme
          ? discoveryCtx.updateBrandSnippets
          : discoveryCtx.updatedBrandThemes;

        const currentList = store || [];
        const updatedList = [...currentList, snippetData]
        updateFn({
          res: updatedList,
        });
        break;
      }

      default:
        console.warn(`Unhandled socket data type: ${type}`);
    }
  };

  //start polling after discovery launch
  const startPolling = (transactionId, type) => {
    if (pollingRef.current) clearInterval(pollingRef.current);

    pollingRef.current = setInterval(() => {
      loadDiscovery(transactionId, type, user?.user_role?.user_id, true);
    }, 1 * 60 * 1000);  // 60 sec
  };

  const retryPolling = (transactionId, type) => {
    if (pollingRef.current) clearInterval(pollingRef.current);

    pollingRef.current = setInterval(() => {
      loadDiscovery(transactionId, type, user?.user_role?.user_id, true);
    }, 30 * 1000); // 30 sec
  };

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    }
  }, [])

  function ProgressIndicator({ total, completed }) {
    const progressPercentage = (completed / total) * 100;

    return (
      <div className={classes.progressIndicator}>
        <div className={classes.progressBar}>
          <div
            className={classes.progressFill}
            style={{
              width: `${progressPercentage}%`,
            }}
          ></div>
        </div>
        <div className={classes.progressText}>
          {completed} of {total} tasks completed
        </div>
      </div>
    );
  }


  const discoveryCall = async (values) => {
    // let category_taxonomies;
    // if (discoveryCtx.type == "product") {
    //   category_taxonomies = discoveryCtx.category_taxonomies ?? discoveryCtx.category_taxonomies;
    // }
    const originalCompetitors = values.competitors || [];
    const brandDomain = values.competitors[0]?.domain
    const competitors = originalCompetitors.slice(1);
    const payload = {
      user_id: user?.user_role?.user_id,
      customerId: user?.customer_id,
      brand: values?.brand?.trim(),
      market: values?.market ? [values?.market] : ["General Market"],
      type: discoveryCtx.type,
      llm: [values?.llm],
      category: [values?.category],
      categoryType: values?.categoryType,
      language: discoveryCtx.language == "it" ? "Italian" : "English",
      competitor: {
        [values.category]: competitors,
      },
      brandDomainName: brandDomain,
      request_type: "discovery",
      shouldProcessImmediately: "true",
      isMock: values.isMock,
      // customTaxonomy: category_taxonomies,
      persona: [{ title: "General Profile", description: "General Profile" }],
      keywords: [
        "Top", "Best", "Most Recommended"
      ],
    }
    try {

      const response = await api.post(
        `${webSocketBaseURL}/brand-discovery`,
        payload
      );
      setPage(1);
      setIsHistoryRefresh(prev => !prev);

      const transactionId = response?.data?.transaction_id;
      const totalCount = response?.data?.totalCount;
      if (transactionId) {
        startPolling(transactionId, discoveryCtx.type);
        discoveryCtx.updateTransactionStatus("RUNNING");
        discoveryCtx.updateSourceValidationStatus("RUNNING");
        discoveryCtx.updateStatus({ totalCount, completedTaskCount: 0 });
      }
      discoveryCtx.updateTransactionId(transactionId);
      discoveryCtx.updateId(transactionId);
      if (!transactionId) {
        console.error("Transaction ID not returned");
        return;
      }
    } catch (error) {
      console.error("Error Occur in Discovery", error);
    }
  };

  console.log("DiscoveryContextData", discoveryCtx)

  return (
    <>
      {/* {externalReportLoading && <LinearProgress />} */}
      <section
      >
        {launchText && <Header actionText={launchText} actionVariant="text" />}
        <div className={classes.page_wrapper}>
          <div className={classes.container}>
            <div className={classes.content}>
              <Tabs
                options={[]}
                onChange={tabChangeHandler}
                selected={tabSelected}
              />
              <Form
                warningText={warningText}
                setWarningText={setWarningText}
                cancelTransaction={cancelTransaction}
                setCancelTransaction={setCancelTransaction}
                stopPolling={stopPolling}
                setIsHistoryRefresh={setIsHistoryRefresh}
                setPage={setPage}
                isHistoryRefresh={isHistoryRefresh}
                page={page}
                id="discovery"
                onReset={() => {
                  setInputValues((prevValues) => {
                    return {
                      ...prevValues,
                      brand: "",
                      category: "",
                      market: "",
                      llm: "",
                    };
                  });
                  setWarningText("");
                }}
                onSubmit={(values) => {
                  if (launchesRemaining == 0 && !isSpecial) {
                    toast.warn(
                      `You have reached your ${launchLimitFrequency} limit`
                    );
                    return;
                  }
                  if (!isSpecial) updateLaunches();
                  if (externalReportLoading) return;
                  if (values.type !== "category") {
                    discoveryCtx.updateEntity(values.brand);
                  }
                  discoveryCtx.updateMarket(values.market);
                  discoveryCtx.updatedCompetitors(values.competitors)
                  discoveryCtx.updateCategory(values.category);
                  discoveryCtx.updateCategoryType(values.categoryType);
                  discoveryCtx.updateLLM(values.llm);
                  discoveryCtx.updateType(discoveryCtx.type);
                  discoveryCtx.updateName(`${values.brand} ${ENTITY_LABELS.brand} Overview`);
                  setTimeout(() => {
                    discoveryCtx.updatedLaunched(true);
                    discoveryCtx.updateLoading(true);
                    setInputValues(values);
                    setTimeout(() => {
                      discoveryCall(values)
                    }, 500)
                  }, 500);
                }}
                onTypeChanged={(val) => {
                  setInputValues((prevVals) => {
                    return {
                      ...prevVals,
                      type: val,
                    };
                  });
                }}
              />
            </div>
            {/* {discoveryType !== "category" && (
            <div className={classes.history}>
              <History
                isHistoryRefresh={isHistoryRefresh}
                setIsHistoryRefresh={setIsHistoryRefresh}
                key={childKey}
                filter={discoveryCtx.type}
                setCancelTransaction={setCancelTransaction}
                startPolling={startPolling}
                stopPolling={stopPolling}
                page={page}
                setPage={setPage}
              />
            </div>
          )} */}
          </div>
          {inlineViewer && (
            <HTMLReportViewer
              html={inlineViewer.html}
              jobId={inlineViewer.jobId}
              transactionId={inlineViewer.transactionId}
              userId={inlineViewer.userId}
              onBack={() => setInlineViewer(null)}
              onSaveUpdate={inlineViewer.onSaveUpdate}
              onSaveNew={inlineViewer.onSaveNew}
            />
          )}
          {!inlineViewer && discoveryCtx.is_launched && (
            <div className={classes.result} id="print-pdf" ref={pdfRef}>
              {(() => {
                const { transactionStatus } = discoveryCtx.state;

                if (transactionStatus === "RETRY") {
                  return (
                    <div className={classes.failMessage}>
                      <div>
                        <span style={{ fontSize: "18px", marginRight: "8px" }}>⚠️</span>
                        Some requests failed. Click Retry to try again and complete this report.
                      </div>
                      <MUIButton onClick={retryDiscovery} loading={processRetry}>
                        Retry
                      </MUIButton>
                    </div>
                  );
                }

                if (transactionStatus === "RUNNING" && processRetry) {
                  return (
                    <div className={classes.retryMessage}>
                      <div>
                        <span style={{ fontSize: "18px", marginRight: "8px" }}>⏳</span>
                        Retry process for missing data is initiated.
                      </div>
                      <MUIButton onClick={retryDiscovery} loading={processRetry}>
                        Retry
                      </MUIButton>
                    </div>
                  );
                }

                return null;
              })()}


              {(discoveryCtx.state.transactionStatus !== "COMPLETED" &&
                discoveryCtx.state.transactionStatus !== "RETRY" &&
                discoveryCtx.is_launched &&
                (discoveryCtx.llm === "gpt-5" ||
                  discoveryCtx.llm === "gpt-5-search-enabled" ||
                  discoveryCtx.llm === 'GeminiV25Search' ||
                  discoveryCtx.llm === "chatgpt_bd")) && (
                  <div className={classes.alertMessage}>
                    <span style={{ fontSize: "18px", marginRight: "8px" }}>🕒</span>
                    This LLM's response may take longer than usual, around 15–20 minutes.
                  </div>
                )}
              {discoveryCtx?.state?.status?.totalCount > 0 &&
                <div className={classes.hideOnPrint}>
                  <ProgressIndicator
                    total={discoveryCtx.state.status.totalCount}
                    completed={discoveryCtx.state.status.completedTaskCount}
                  />
                </div>
              }
              <PDFHeader />
              {discoveryType !== "category" && (
                <BrandScore
                  category={inputValues.category}
                  market={inputValues.market}
                  entity={inputValues.brand}
                  remountChild={remountChild}
                  onViewInlineOpen={(data) => setInlineViewer(data)}
                />
              )}
              {/* <div className="page-break print-only" /> */}
              <div className={classes.section}>
                {competitors.length > 0 && (
                  <Competitors
                    category={inputValues.category}
                    entity={inputValues.brand}
                  />
                )}
              </div>
              {discoveryType === "brand" && discoveryCtx?.core_attributes && (
                <div className={classes.section_summary}>
                  <div className={classes.flex_one}>
                    {competitors.length > 0 && (
                      <CoreAttributes
                        category={inputValues.category}
                        entity={entity}
                      />
                    )}
                  </div>
                  <div className={classes.flex_one}>
                    <AIGenerated
                      edit
                      text={coreAttributesSummary}
                      loading={coreAttributesSummary === ""}
                      firebaseNode="core_attributes"
                    />
                  </div>
                </div>
              )}

              {discoveryType === "category" && (
                <div className={classes.section_summary}>
                  <div className={classes.flex_one}>
                    {competitors && competitors.length > 0 && (
                      <CategoryDimensions category={inputValues.category}  />
                    )}
                  </div>
                  <div className={classes.flex_one}>
                    <AIGenerated
                      edit={discoveryType != "category" || !discoveryCtx.loading}
                      text={categoryDimensionsSummary}
                      loading={categoryDimensionsSummary === ""}
                      firebaseNode="category_dimensions"
                    />
                  </div>
                </div>
              )}

              {(discoveryType === "product" || discoveryType === "category") && (
                <div className={classes.section_summary}>
                  <div className={classes.flex_one}>
                    {competitors && competitors.length > 0 && <Taxonomy />}
                  </div>
                  <div className={classes.flex_one}>
                    <AIGenerated
                      edit={discoveryType != "category" || !discoveryCtx.loading}
                      text={productDimensionSummary}
                      loading={productDimensionSummary === ""}
                      firebaseNode="taxonomies"
                    />
                  </div>
                </div>
              )}

              {discoveryType === "category" && (
                <div className={classes.product_recommendation_container}>
                  {competitors && competitors.length > 0 && <ProductSpecs />}
                </div>
              )}

              {/* <div className="page-break print-only" /> */}
              {discoveryType != "category" && (
                <div className={classes.section_summary}>
                  <div className={classes.flex_one}>
                    {competitors && competitors.length > 0 && (
                      <CategoryDimensions
                        category={inputValues.category}
                        entity={entity}
                      />
                    )}
                  </div>
                  <div className={classes.flex_one}>
                    <AIGenerated
                      edit={!discoveryCtx.loading}
                      text={categoryDimensionsSummary}
                      loading={categoryDimensionsSummary === ""}
                      firebaseNode="category_dimensions"
                    />
                  </div>
                </div>
              )}
              {/* <div className="page-break print-only" /> */}
              {discoveryType != "category" && (
                <div className={`${classes.section} pdf-sources-section`}>
                  <Sources />
                </div>
              )}
              {discoveryType !== "category" && (
                <div className={classes.section}>
                  <RealtimeActions />
                </div>
              )}

              {(!discoveryCtx.state.transactionStatus) && (
                <div className={`${classes.action_btn_group} no-print`}>
                  <MUIButton onClick={exportToPDF}>Export</MUIButton>
                </div>
              )}

              {(discoveryCtx.state.transactionStatus === "COMPLETED") && discoveryType != "category" && (
                <div className={`${classes.action_btn_group} no-print`}>
                  <MUIButton onClick={exportToPDF}>Export</MUIButton>
                  <MUIButton onClick={generateReport} loading={loading}>
                    Generate Report
                  </MUIButton>
                </div>
              )}
              <PDFFooter />
            </div>
          )}
        </div>
      </section>
    </>
  );
}